import React from 'react';

import { Button, Container, Paper, Typography, Box } from '@mui/material';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import RefreshIcon from '@mui/icons-material/Refresh';
import { isDevelopment } from '../../utils/environment';

/**
 * @class ErrorBoundary
 * @description Componente de boundary para capturar erros
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { 
      hasError: false,
      error: null,
      errorInfo: null,
      errorCount: 0
    };
  }

  /**
   * @function getDerivedStateFromError
   * @description Atualiza o state para renderizar a UI de fallback
   * @param {Error} error - Erro capturado
   * @returns {Object} - State atualizado
   */
  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  /**
   * @function componentDidCatch
   * @description Captura erros e atualiza o state
   * @param {Error} error - Erro capturado
   * @param {Object} errorInfo - Informações do erro
   */
  componentDidCatch(error, errorInfo) {
    console.error('ErrorBoundary capturou um erro:', error, errorInfo);
    
    this.setState(prevState => ({
      error: error,
      errorInfo: errorInfo,
      errorCount: prevState.errorCount + 1
    }));
  }

  /**
   * @function handleReload
   * @description Limpa o erro e recarrega a página
   */
  handleReload = () => {
    this.setState({ 
      hasError: false, 
      error: null, 
      errorInfo: null 
    });
    window.location.reload();
  };

  handleReset = () => {
    this.setState({ 
      hasError: false, 
      error: null, 
      errorInfo: null 
    });
  };

  /**
   * @function render
   * @description Renderiza a UI de fallback quando há erro
   * @returns {React.ReactNode} - UI de fallback
   */
  render() {
    if (this.state.hasError) {
      return (
        <Container 
          maxWidth="md" 
          sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center', 
            minHeight: '100vh',
            bgcolor: '#f5f5f5'
          }}
        >
          <Paper 
            elevation={3} 
            sx={{ 
              p: 4, 
              textAlign: 'center',
              borderRadius: 2
            }}
          >
            <ErrorOutlineIcon 
              sx={{ 
                fontSize: 80, 
                color: 'error.main',
                mb: 2
              }} 
            />
            
            <Typography variant="h4" gutterBottom color="error">
              Ops! Algo deu errado
            </Typography>
            
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              Ocorreu um erro inesperado. Por favor, tente recarregar a página.
            </Typography>

            {isDevelopment() && this.state.error && (
              <Box 
                sx={{ 
                  mt: 3, 
                  p: 2, 
                  bgcolor: '#f5f5f5', 
                  borderRadius: 1,
                  textAlign: 'left',
                  maxHeight: 300,
                  overflow: 'auto'
                }}
              >
                <Typography variant="subtitle2" color="error" gutterBottom>
                  Detalhes do Erro (apenas em desenvolvimento):
                </Typography>
                <Typography variant="body2" component="pre" sx={{ fontSize: '0.75rem' }}>
                  {this.state.error.toString()}
                </Typography>
                {this.state.errorInfo && (
                  <Typography variant="body2" component="pre" sx={{ fontSize: '0.75rem', mt: 1 }}>
                    {this.state.errorInfo.componentStack}
                  </Typography>
                )}
              </Box>
            )}

            <Box sx={{ mt: 4, display: 'flex', gap: 2, justifyContent: 'center' }}>
              <Button
                variant="contained"
                color="primary"
                startIcon={<RefreshIcon />}
                onClick={this.handleReload}
                size="large"
              >
                Recarregar Página
              </Button>
              
              <Button
                variant="outlined"
                color="primary"
                onClick={this.handleReset}
                size="large"
              >
                Tentar Novamente
              </Button>
            </Box>

            {this.state.errorCount > 1 && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
                Este erro ocorreu {this.state.errorCount} vezes
              </Typography>
            )}
          </Paper>
        </Container>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;

